import { Box, Button, Card, Chip, ListItemButton, Stack, Typography, formatPhoneNumber } from "@compass/core";
import {
  BroadcastConversationUpdate,
  TextingConversationListDto,
  TextingConversationListFilter,
  TextingConversationsHubAction,
} from "generated/openApi";
import { useDateTime } from "hooks";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { ConnectionStatus } from "../ConnectionStatus";
import { useTextingConversationsHubConnection } from "../useTextingConversationsHubConnection";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useOutboundTextingConversationsHubConnection } from "../useOutboundTextingConversationsHubConnection";

export const Conversations = (props: {
  personTypeId: number;
  selectedConversationId: number | undefined;
  setSelectedConversationId: (value: number | undefined) => void;
}) => {
  const { textingConversationsHubConnection, connectionStatus } = useTextingConversationsHubConnection();
  // const { outboundTextingConversationsHubConnection, outboundConnectionStatus } = useOutboundTextingConversationsHubConnection();

  // contains real time conversation updates
  const [conversationUpdates, setConversationUpdates] = useState<BroadcastConversationUpdate[]>([]);

  const [conversations, setConversations] = useState<TextingConversationListDto[]>([]);
  const [listFilter, setListFilter] = useState<TextingConversationListFilter>({
    pageSize: 50,
    skip: 0,
    conversationFilter: { personTypeId: props.personTypeId, hasFlaggedMessages: false, unseenMessages: false },
  });
  const [total, setTotal] = useState<number>();
  const formatDateTime = useDateTime();

  // handle real time incoming updates
  useEffect(() => {
    const handleIncomingUpdate = (incomingJsonString: string) => {
      // store update info
      const conversationUpdate = JSON.parse(incomingJsonString) as BroadcastConversationUpdate;
      setConversationUpdates((prev) => [...prev, conversationUpdate]);

      const filter = { ...listFilter };
      filter.conversationFilter!.personTypeId = props.personTypeId;
  
      load(filter);
    };

    textingConversationsHubConnection.on(
      TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
      handleIncomingUpdate
    );

    // textingConversationsHubConnection.off(
    //   TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
    //   handleIncomingUpdate
    // );

    return () => {
      textingConversationsHubConnection.off(
        TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
        handleIncomingUpdate
      );
    };

    // outboundTextingConversationsHubConnection.on(
    //   TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
    //   handleIncomingUpdate
    // );

    // return () => {
    //   outboundTextingConversationsHubConnection.off(
    //     TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
    //     handleIncomingUpdate
    //   );
    // };
  }, [textingConversationsHubConnection, props.personTypeId]);

  useEffect(() => {
    const filter = { ...listFilter };
    filter.conversationFilter!.personTypeId = props.personTypeId;

    load(filter);

    setConversationUpdates([]);
  }, [props.personTypeId]);

  const fetchList = (listFilter: TextingConversationListFilter) => ApiService.textingConversations.getList(listFilter);

  const fetchTotal = (listFilter: TextingConversationListFilter) =>
    ApiService.textingConversations.getListTotal(listFilter);

  const load = (filter: TextingConversationListFilter) => {
    fetchList(filter).then((response) => {
      setConversations(response);
    });

    fetchTotal(filter).then((response) => {
      setTotal(response);
    });
  };

  const loadMore = () => {
    fetchList({ ...listFilter, skip: conversations.length }).then((response) => {
      setConversations([...conversations, ...response]);
    });
  };

  const handleConversationClick = (conversationId: number) => {
    props.setSelectedConversationId(conversationId);

    const _conversations = [...conversations];
    const conversation = _conversations.find((c) => c.id === conversationId);
    conversation!.unseenMessages = 0;
    setConversations(_conversations);

    ApiService.textingConversations.clearUnseenMessages(conversationId);
  };

  const renderConversationListItem = (conversation: TextingConversationListDto, index: number) => {
    const selected = props.selectedConversationId === conversation.id;

    return (
      <ListItemButton
        key={`${conversation.id}:${index}`}
        onClick={() => handleConversationClick(conversation.id!)}
        selected={selected}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.border.main}`,
          transition: "all 0.2s",
          "&.Mui-selected": {
            backgroundColor: "rgba(25, 118, 210, 0.08)",
            borderLeft: "3px solid",
            borderLeftColor: "primary.main",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.12)",
            },
          },
        }}
      >
        <Stack width="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color={selected ? "primary.main" : "text.primary"}
              noWrap
              sx={{ maxWidth: "70%" }}
            >
              {conversation.personName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              {formatDateTime(conversation.lastMessageDate)}
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: "0.01em" }}>
            {formatPhoneNumber(conversation.destinationPhoneNumber)}
          </Typography>

          <Stack direction="row" spacing={"sm"} alignItems="center" sx={{ width: "100%" }}>
            <Typography
              variant="caption"
              noWrap
              // color={(theme) =>
              //   conversation.isLastMessageFromDestination ? theme.palette.primary.main : theme.palette.text.primary
              // }
              sx={{
                flex: 1,
                minWidth: 0, // This is crucial for text truncation to work
              }}
            >
              {conversation.lastMessage}
            </Typography>

            {conversation.unseenMessages !== undefined && conversation.unseenMessages > 0 && (
              <Chip
                size="small"
                label={conversation.unseenMessages}
                color="primary"
                sx={{
                  minWidth: "24px",
                  height: "24px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              />
            )}
          </Stack>
        </Stack>
      </ListItemButton>
    );
  };

  const howManyMore = total ? total - conversations.length : undefined;
  const hasMore = howManyMore && howManyMore > 0 ? true : false;

  function updateButton() {
    if (conversationUpdates.length === 0) return null;

    const uniqueConversations = new Set(conversationUpdates.map((u) => u.conversationId)).size;

    function handleClick() {
      load({ ...listFilter, skip: 0 });
      setConversationUpdates([]);
    }

    return (
      <Box>
        <Button onClick={handleClick} variant="outlined" size="small" textTransform="none">
          <Typography variant="inherit">{uniqueConversations} updates</Typography>

          <RefreshIcon fontSize="small" />
        </Button>
      </Box>
    );
  }

  return (
    <Card style={{ height: "100%" }} bodySx={{ padding: 0 }}>
      <Stack width="100%" direction="column" justifyContent="space-between">
        <Stack
          direction="row"
          justifyContent={"space-between"}
          alignItems="center"
          borderBottom={1}
          borderColor="divider"
          px={2}
          py={1}
        >
          <ConnectionStatus status={connectionStatus} />

          {updateButton()}
        </Stack>

        {conversations.map((s, index) => renderConversationListItem(s, index))}

        <Stack>{hasMore && <Button onClick={loadMore}>{howManyMore} More</Button>}</Stack>
      </Stack>
    </Card>
  );
};
